export type ExcelExportColumn = {
  /** Column header in the exported Excel file */
  header: string;
  /** Key from onboarding row data; when omitted, defaultValue is always used */
  sourceField?: string;
  /** Value when sourceField is missing, null, or empty string */
  defaultValue?: string | number | boolean | null;
};

/** Transforms onboarding sheet data into flat rows before column mapping. */
export type ExcelExportRowMapper = (
  sheetData: unknown,
) => Record<string, unknown>[];

/** Visual One import sequencing — lower importOrder runs first. */
export type ExcelImportMeta = {
  /** Sort key; worksheets with the same phase run in one API request batch. */
  importOrder: number;
  importPhase?: string;
  /** Worksheet tab names that must be imported before this sheet. */
  dependsOn?: string[];
};

export type ExcelExportWorksheetConfig = {
  /** Worksheet tab name inside the .xlsx file */
  worksheetName: string;
  columns: ExcelExportColumn[];
  /** Headers only — no data rows (for import template sub-sheets) */
  templateOnly?: boolean;
  /** Include worksheet tab (headers only) even when mapped rows are empty */
  includeWhenEmpty?: boolean;
  /** Optional row transform (e.g. explode multi-select fields into multiple rows) */
  mapRows?: ExcelExportRowMapper;
  /** Import sequencing (overrides product registry when set). */
  importOrder?: number;
  importPhase?: string;
  dependsOn?: string[];
};

export type ExcelExportSheetConfig = {
  /** Payload key from onboarding (e.g. SpaPropertyEnum.services) */
  payloadKey: string;
  /** File name for the .xlsx inside the zip (without extension) */
  fileName: string;
  worksheets: ExcelExportWorksheetConfig[];
};

/** Helper to define a column with optional onboarding field mapping and default */
export const excelColumn = (
  header: string,
  sourceField?: string,
  defaultValue?: string | number | boolean | null,
): ExcelExportColumn => ({
  header,
  // Empty string means "no onboarding field" (same as omitting sourceField).
  ...(sourceField ? { sourceField } : {}),
  ...(defaultValue !== undefined && { defaultValue }),
});

export const shouldIncludeWorksheetInExport = (
  worksheet: ExcelExportWorksheetConfig,
  rowCount: number,
): boolean =>
  Boolean(worksheet.templateOnly) ||
  Boolean(worksheet.includeWhenEmpty) ||
  rowCount > 0;

export const excelWorksheet = (
  worksheetName: string,
  columns: ExcelExportColumn[],
  templateOnly?: boolean,
  mapRows?: ExcelExportRowMapper,
  importMeta?: ExcelImportMeta,
  includeWhenEmpty?: boolean,
): ExcelExportWorksheetConfig => ({
  worksheetName,
  columns,
  ...(templateOnly ? { templateOnly: true } : {}),
  ...(includeWhenEmpty ? { includeWhenEmpty: true } : {}),
  ...(mapRows ? { mapRows } : {}),
  ...(importMeta?.importOrder !== undefined
    ? { importOrder: importMeta.importOrder }
    : {}),
  ...(importMeta?.importPhase ? { importPhase: importMeta.importPhase } : {}),
  ...(importMeta?.dependsOn?.length ? { dependsOn: importMeta.dependsOn } : {}),
});

/** One or more worksheets inside a single exported .xlsx file */
export const excelFile = (
  payloadKey: string,
  fileName: string,
  worksheets: ExcelExportWorksheetConfig[],
): ExcelExportSheetConfig => ({
  payloadKey,
  fileName,
  worksheets,
});

/** Single-worksheet file (worksheet name matches file name by default) */
export const excelSheet = (
  payloadKey: string,
  fileName: string,
  columns: ExcelExportColumn[],
  worksheetName?: string,
  mapRows?: ExcelExportRowMapper,
): ExcelExportSheetConfig =>
  excelFile(payloadKey, fileName, [
    excelWorksheet(worksheetName ?? fileName, columns, false, mapRows),
  ]);
